import MuiFormControl from '@mui/material/FormControl';
import MuiFormHelperText from '@mui/material/FormHelperText';
import MuiFormLabel from '@mui/material/FormLabel';
import MuiRadioGroup from '@mui/material/RadioGroup';
import * as React from 'react';

import { styled } from '../styled';
import { firstLetterUppercase, sentence, Typography } from '../typography';
import { BaseProps, withVersion } from '../utils';
import { RadioGroupContext } from './context';

const StyledRadioGroup = styled(MuiRadioGroup)(({ theme }) => ({
    gap: 0,
    '&.MuiFormGroup-row': {
        columnGap: theme.spacing(3),
        rowGap: 0
    },
    '& .MuiFormControlLabel-root': {
        margin: 0
    }
}));

export type RadioGroupProps = BaseProps & {
    label?: React.ReactNode;
    helperText?: React.ReactNode;
    error?: React.ReactNode;
    row?: boolean;
    value?: unknown;
    defaultValue?: unknown;
    name?: string;
    onChange?(value: string, event: React.ChangeEvent<HTMLInputElement>): void;
    children: React.ReactNode;
};

const RadioGroupImpl = (props: RadioGroupProps) => {
    const {
        label,
        helperText,
        error,
        row,
        value,
        defaultValue,
        name,
        onChange,
        children,
        className,
        'data-role': dataRole,
        'data-role-value': dataRoleValue
    } = props;

    return (
        <RadioGroupContext.Provider value={true}>
            <MuiFormControl
                component="fieldset"
                className={className}
                error={!!error}
                data-role={dataRole}
                data-role-value={dataRoleValue}
            >
                {label && (
                    <MuiFormLabel component="legend">
                        <Typography textTransform="sentence">
                            {label}
                        </Typography>
                    </MuiFormLabel>
                )}
                <StyledRadioGroup
                    row={row}
                    value={value}
                    defaultValue={defaultValue}
                    name={name}
                    onChange={(e) => onChange?.(e.target.value, e)}
                >
                    {children}
                </StyledRadioGroup>
                {error ? (
                    <MuiFormHelperText>{sentence(error)}</MuiFormHelperText>
                ) : (
                    helperText && (
                        <MuiFormHelperText sx={{ ...firstLetterUppercase }}>
                            {helperText}
                        </MuiFormHelperText>
                    )
                )}
            </MuiFormControl>
        </RadioGroupContext.Provider>
    );
};

export const RadioGroup = withVersion(RadioGroupImpl, '1.0.0');
